import { rectIntersection, DndContext } from '../adapters/dndAdapter';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import useDndSensors from './useDndSensors';

const DRAGGING_CLASS = 'is-dragging-active';

const noAnnouncement = () => '';
const ANNOUNCEMENT_FALLBACKS = {
  onDragStart: noAnnouncement,
  onDragOver: noAnnouncement,
  onDragEnd: noAnnouncement,
  onDragCancel: noAnnouncement,
};

/**
 * DndProvider - Wrapper de DndContext con sensores preconfigurados
 *
 * Proporciona el contexto de drag and drop para todos los componentes hijos
 *
 * @param {React.ReactNode} children - Contenido del contexto
 * @param {Function} onDragEnd - Callback cuando termina el drag
 * @param {Function} [onDragStart] - Callback cuando inicia el drag
 * @param {Function} [onDragOver] - Callback durante el drag
 * @param {Function} [onDragCancel] - Callback cuando se cancela
 * @param {Array} [sensors] - Sensores personalizados (opcional, usa default si no se provee)
 * @param {Function} [collisionDetection] - Estrategia de colisión (default: closestCenter)
 * @param {Object|boolean} [autoScroll] - Config de autoscroll de @dnd-kit (p. ej. excluir contenedores o desactivar la compensación de layout)
 * @param {Object} [accessibility] - Config de accesibilidad de @dnd-kit (announcements, screenReaderInstructions)
 * @param {string} [className] - Clase CSS adicional para el contenedor
 */
const DndProvider = ({
  children,
  onDragEnd,
  onDragStart,
  onDragOver,
  onDragCancel,
  sensors: customSensors,
  collisionDetection = rectIntersection,
  autoScroll,
  accessibility,
  className,
}) => {
  const defaultSensors = useDndSensors();
  const sensors = customSensors || defaultSensors;

  // @dnd-kit invoca announcements.onDragOver sin comprobar que exista, así que
  // un announcements parcial revienta. Rellenamos los handlers ausentes con no-ops.
  const safeAccessibility = useMemo(() => {
    if (!accessibility?.announcements) return accessibility;
    return {
      ...accessibility,
      announcements: { ...ANNOUNCEMENT_FALLBACKS, ...accessibility.announcements },
    };
  }, [accessibility]);

  // Añadir clase al body cuando empieza el drag (para cursor grabbing global)
  const handleDragStart = useCallback(
    (event) => {
      document.body.classList.add(DRAGGING_CLASS);
      onDragStart?.(event);
    },
    [onDragStart]
  );

  // Quitar clase del body cuando termina el drag
  const handleDragEnd = useCallback(
    (event) => {
      document.body.classList.remove(DRAGGING_CLASS);
      onDragEnd?.(event);
    },
    [onDragEnd]
  );

  // Quitar clase del body cuando se cancela el drag
  const handleDragCancel = useCallback(
    (event) => {
      document.body.classList.remove(DRAGGING_CLASS);
      onDragCancel?.(event);
    },
    [onDragCancel]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      autoScroll={autoScroll}
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      accessibility={safeAccessibility}
    >
      {className ? <div className={className}>{children}</div> : children}
    </DndContext>
  );
};

DndProvider.propTypes = {
  children: PropTypes.node.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragCancel: PropTypes.func,
  sensors: PropTypes.array,
  collisionDetection: PropTypes.func,
  autoScroll: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  accessibility: PropTypes.object,
  className: PropTypes.string,
};

export default DndProvider;
